import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  IconButton,
  InputAdornment,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAISearch } from '../../hooks/useAISearch';
import { ISearchResult } from '../../api/search';
import { RECIPE } from '../../routes';

interface AISearchDrawerProps {
  open: boolean;
  onClose: () => void;
}

const AISearchDrawer: React.FC<AISearchDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const { results, loading, total, search, clearResults } = useAISearch();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!open) {
      setQuery('');
      clearResults();
    }
  }, [open, clearResults]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;
    await search(query);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleResultClick = (result: ISearchResult) => {
    if (result.type === 'recipe') {
      navigate(`${RECIPE}${result.id}`);
    }
    onClose();
  };

  const hasSearched = total > 0 || (results.length === 0 && !loading && query.trim().length > 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 }, p: 2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('search.aiSearch')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <TextField
        fullWidth
        inputRef={inputRef}
        placeholder={t('search.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyPress={handleKeyPress}
        size="small"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            top: 0,
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSearch}
                disabled={!query.trim()}
                size="small"
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mt: 2, flexGrow: 1, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && hasSearched && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {t('search.noResultsFor')} "{query}"
          </Typography>
        )}

        {!loading && results.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t(results.length === 1 ? 'search.recipesFound' : 'search.recipesFound_plural', {
                count: total,
              })}
            </Typography>
            <List disablePadding>
              {results.map((result) => (
                <ListItemButton
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {result.name}
                        <Chip label={result.type} size="small" variant="outlined" />
                      </Box>
                    }
                    secondary={result.description}
                    secondaryTypographyProps={{
                      noWrap: true,
                      sx: { maxWidth: 300 },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {!loading && !hasSearched && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {t('search.startTyping')}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default AISearchDrawer;
